import { useConfigStore } from '@/stores/configStore';
import { defaultConfig } from '@/stores/configStore';
import { useAccessibilityTTS } from '@/hooks/useAccessibilityTTS';
import useTickSound from '@/hooks/useTickSound';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef } from 'react';
import { convertNumberToKorean } from '@/utils/number';

export const useVolumeButton = (onBarrierFreeUsed?: () => void) => {
    const { speak } = useAccessibilityTTS();
    const { playTick } = useTickSound();
    const voice = useConfigStore((state) => state.config.tts.voice);
    const volume = useConfigStore((state) => state.config.ui.volume);
    const updateConfig = useConfigStore((state) => state.updateConfig);
    const { t } = useTranslation();
    const language = useConfigStore((state) => state.config.ui.language);
    const isClickingRef = useRef(false);

    const handleVolumeChange = useCallback(() => {
        // Set flag to prevent focus handler from running
        isClickingRef.current = true;

        const currentVolume = volume || 1;
        const nextVolume = currentVolume >= 4 ? 1 : currentVolume + 1;

        // Play tick sound with the new volume immediately
        playTick('tick', nextVolume);

        // Update volume in config
        updateConfig({
            ui: {
                ...useConfigStore.getState().config.ui,
                volume: nextVolume,
            },
        });

        // Provide TTS feedback if voice is enabled
        if (voice) {
            if (language === 'ko') {
                speak(t('footer.barrier_free.volume_level', { level: convertNumberToKorean(nextVolume) }), undefined, nextVolume);
            } else {
                speak(t('footer.barrier_free.volume_level', { level: nextVolume }), undefined, nextVolume);
            }
        }

        // Call barrier free callback if provided
        onBarrierFreeUsed?.();

        // Reset flag after a short delay
        setTimeout(() => {
            isClickingRef.current = false;
        }, 100);
    }, [volume, updateConfig, voice, speak, t, onBarrierFreeUsed, playTick]);

    const handleVolumeFocus = useCallback(() => {
        const currentVolume = volume || 1;
        // Don't run focus handler if we just clicked
        if (isClickingRef.current) {
            return;
        }

        if (voice) {
            if (language === 'ko') {
                speak(t('footer.barrier_free.volume_level', { level: convertNumberToKorean(currentVolume) }), undefined, currentVolume);
            } else {
                speak(t('footer.barrier_free.volume_level', { level: currentVolume }), undefined, currentVolume);
            }
        }
    }, [voice, speak, t]);

    const handleVolumeKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                handleVolumeChange();
            }
        },
        [handleVolumeChange]
    );

    return {
        volume,
        handleVolumeChange,
        handleVolumeFocus,
        handleVolumeKeyDown,
        isVolumeChanged: volume !== defaultConfig.ui.volume,
    };
};

export default useVolumeButton;
